import { useState, useEffect } from 'react';
import apiClient from '@/lib/axios';
import type { Estimator, ApiResponse } from '../types';

/**
 * Hook to fetch active Project Managers for lead assignment.
 * Fetches users with role="Project Manager" and isActive=true and
 * sorts the results by lastName, then firstName (case-insensitive).
 *
 * The returned shape uses the legacy `Estimator` type; the type rename
 * (Estimator → ProjectManager) is deferred to a future sweep so the
 * commissions feature does not need to change today.
 */
export function useProjectManagers() {
  const [projectManagers, setProjectManagers] = useState<Estimator[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProjectManagers = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await apiClient.get<ApiResponse<Estimator[]>>('/users', {
          params: {
            role: 'Project Manager',
            isActive: true,
          },
        });

        const sorted = [...response.data.data].sort((a, b) => {
          const lastCompare = a.lastName.localeCompare(b.lastName, undefined, {
            sensitivity: 'base',
          });
          if (lastCompare !== 0) return lastCompare;
          return a.firstName.localeCompare(b.firstName, undefined, {
            sensitivity: 'base',
          });
        });
        setProjectManagers(sorted);
      } catch (err) {
        const error = err as { response?: { data?: { message?: string } } };
        setError(error.response?.data?.message || 'Failed to fetch project managers');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjectManagers();
  }, []);

  return {
    projectManagers,
    isLoading,
    error,
  };
}
